<template>
  <div>
    <b-overlay :show="loading">
      <b-card>
        <!-- Header -->
        <div class="mb-3">
          <h4 class="mb-0">{{ isEdit ? "Edit Shift" : "Create New Shift" }}</h4>
          <small class="text-muted">{{
            isEdit
              ? "Update driver shift configuration"
              : "Add a new driver shift configuration"
          }}</small>
        </div>

        <!-- Form -->
        <b-form @submit.prevent="handleSubmit">
          <validation-observer ref="shiftFormRef">
            <b-row>
              <b-col md="12">
                <validation-provider
                  #default="{ errors }"
                  name="Shift Name"
                  rules="required"
                >
                  <b-form-group label="Shift Name" label-for="shift-name">
                    <b-form-input
                      id="shift-name"
                      v-model.trim="form.name"
                      maxlength="100"
                      placeholder="Enter shift name"
                      @input="handleNameInput"
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                    <small class="text-muted">{{ form.name.length }}/100</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <validation-provider
              #default="{ errors }"
              name="Description"
              rules="required"
            >
              <b-form-group label="Description" label-for="shift-description">
                <b-form-textarea
                  id="shift-description"
                  v-model="form.description"
                  placeholder="Enter shift description"
                  rows="3"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="{ errors }"
              name="Days"
              rules="required"
            >
              <b-form-group label="Days">
                <b-form-checkbox-group
                  v-model="form.days"
                  :options="weekdays"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </b-form-group>
            </validation-provider>

            <b-row>
              <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="Start Date"
                  rules="required"
                >
                  <b-form-group label="Start Date" label-for="start-date">
                    <b-form-datepicker
                      id="start-date"
                      v-model="form.start_date"
                      :min="minDate"
                      placeholder="Select start date"
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="Start Time"
                  rules="required"
                >
                  <b-form-group label="Start Time" label-for="start-time">
                    <b-form-timepicker
                      id="start-time"
                      v-model="form.start_time"
                      placeholder="Select start time"
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="End Date"
                  rules="required"
                >
                  <b-form-group label="End Date" label-for="end-date">
                    <b-form-datepicker
                      id="end-date"
                      v-model="form.end_date"
                      :min="minDate"
                      placeholder="Select end date"
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="End Time"
                  rules="required"
                >
                  <b-form-group label="End Time" label-for="end-time">
                    <b-form-timepicker
                      id="end-time"
                      v-model="form.end_time"
                      placeholder="Select end time"
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <b-row>
              <!-- <b-col md="6">
                <b-form-group label="Group (Optional)" label-for="group-select">
                  <v-select
                    id="group-select"
                    v-model="form.group_name"
                    placeholder="Search and select group"
                    :options="groupsList"
                    label="name"
                    :reduce="(val) => val.id"
                    @search="fetchGroups"
                    clearable
                  />
                </b-form-group>
              </b-col> -->
              <b-col md="6">
                <b-form-group>
                  <b-form-checkbox v-model="form.is_active" name="is-active">
                    Active
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-end">
              <b-button type="submit" variant="primary" :disabled="loading">
                <feather-icon
                  v-if="loading"
                  icon="LoaderIcon"
                  class="mr-50"
                  size="16"
                />
                {{ isEdit ? "Update Shift" : "Create Shift" }}
              </b-button>
            </div>
          </validation-observer>
        </b-form>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckboxGroup,
  BFormCheckbox,
  BFormDatepicker,
  BFormTimepicker,
  BRow,
  BCol,
  BOverlay,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, reactive, onMounted, computed } from "@vue/composition-api";
import axiosIns from "@/libs/axios";
import {
  errorNotification,
  successNotification,
} from "@/utils/toast-notifications";
import { required } from "@validations";
import vSelect from "vue-select";
import { debounce } from "lodash";

const weekdays = [
  { value: 1, text: "Monday" },
  { value: 2, text: "Tuesday" },
  { value: 3, text: "Wednesday" },
  { value: 4, text: "Thursday" },
  { value: 5, text: "Friday" },
  { value: 6, text: "Saturday" },
  { value: 7, text: "Sunday" },
];

export default {
  components: {
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckboxGroup,
    BFormCheckbox,
    BFormDatepicker,
    BFormTimepicker,
    BRow,
    BCol,
    BOverlay,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  setup(_, { root }) {
    const loading = ref(false);
    const shiftData = ref(null);
    const shiftFormRef = ref(null);

    const isEdit = computed(() => !!root.$route.params.id);

    const form = reactive({
      name: "",
      description: "",
      days: [],
      start_date: "",
      start_time: "",
      end_date: "",
      end_time: "",
      // group_name: "",
      is_active: true,
    });

    const groupsList = ref([]);
    const minDate = new Date().toISOString().slice(0, 10); // Today's date in YYYY-MM-DD format

    const fetchShift = async () => {
      if (!isEdit.value) return;

      try {
        loading.value = true;
        const response = await axiosIns.get(
          `/drivers/shifts/${root.$route.params.id}`
        );
        shiftData.value = response.data.data;

        // Populate form with shift data
        form.name = shiftData.value.name;
        form.description = shiftData.value.description || "";
        form.days = shiftData.value.days || [];
        form.start_date = formatDateForInput(shiftData.value.start_time);
        form.start_time = formatTimeForInput(shiftData.value.start_time);
        form.end_date = formatDateForInput(shiftData.value.end_time);
        form.end_time = formatTimeForInput(shiftData.value.end_time);
        // form.group_name = shiftData.value.group_name || "";
        form.is_active = shiftData.value.is_active;
      } catch (error) {
        console.error("Error fetching shift:", error);
        errorNotification("Failed to fetch shift details");
        root.$router.push({ name: "drivers-shifts" });
      } finally {
        loading.value = false;
      }
    };

    const formatDateForInput = (dateTimeString) => {
      if (!dateTimeString) return "";
      const date = new Date(dateTimeString);
      return date.toISOString().slice(0, 10); // Returns YYYY-MM-DD format
    };

    const formatTimeForInput = (dateTimeString) => {
      if (!dateTimeString) return "";
      const date = new Date(dateTimeString);
      return date.toTimeString().slice(0, 5); // Returns HH:MM format
    };

    const fetchGroups = debounce(async (search, loading) => {
      if (!search) return;
      loading(true);

      try {
        const result = await axiosIns.get("/groups", {
          params: {
            name: search,
          },
        });
        const { data } = result.data.data;
        groupsList.value = data;
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        loading(false);
      }
    }, 1000);

    const handleSubmit = async () => {
      const valid = await shiftFormRef.value.validate();
      if (!valid) return;

      try {
        loading.value = true;

        // Create proper ISO date strings
        const startDateTime = new Date(`${form.start_date}T${form.start_time}`);
        const endDateTime = new Date(`${form.end_date}T${form.end_time}`);

        const shiftData = {
          name: form.name.trim(),
          description: form.description.trim(),
          days: form.days,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          // group_name: form.group_name || undefined,
          is_active: form.is_active,
        };

        if (isEdit.value) {
          await axiosIns.put(
            `/drivers/shifts/${root.$route.params.id}`,
            shiftData
          );
          successNotification("Shift updated successfully");
        } else {
          await axiosIns.post("/drivers/shifts", shiftData);
          successNotification("Shift created successfully");
        }

        root.$router.push({ name: "drivers-shifts" });
      } catch (error) {
        console.error("Error saving shift:", error?.response);
        errorNotification("Failed to save shift");
      } finally {
        loading.value = false;
      }
    };

    const handleNameInput = () => {
      nameExceeded.value = form.name.length > 100;
    };

    onMounted(() => {
      fetchShift();
    });

    return {
      handleNameInput,
      loading,
      isEdit,
      form,
      required,
      weekdays,
      shiftFormRef,
      handleSubmit,
      groupsList,
      fetchGroups,
      minDate,
    };
  },
};
</script>

<style scoped>
.gap-2 > * + * {
  margin-left: 0.5rem;
}
</style>
